import type { IChatConversation, IChatMessage } from "@/types/(my-space)/chat.interface";

/** Like WhatsApp: one forward reaches at most five chats (the API's cap too). */
export const MAX_FORWARD_TARGETS = 5;
/** From this many forwards a copy reads "Forwarded many times". */
export const FORWARDED_MANY_TIMES = 5;

/**
 * Forward is offered on a sent, live, non-system message — except someone
 * else's shared item (its snapshot is the owner's to hand out).
 */
export const canForward = (
  m: Pick<IChatMessage, "id" | "type" | "isDeleted" | "metadata"> & { sendState?: string },
  myUserId: number | undefined,
) => {
  if (m.id <= 0 || m.isDeleted || m.sendState || m.type === "system") return false;
  const owner = m.metadata?.shortcut?.entity?.ownerId;
  return owner == null || owner === myUserId;
};

/** "Forwarded" / "Forwarded many times" / null for a message that isn't a forward. */
export const forwardedKind = (m: Pick<IChatMessage, "metadata">) => {
  const count = m.metadata?.forwarded?.count ?? 0;
  if (count <= 0) return null;
  return count >= FORWARDED_MANY_TIMES ? ("many" as const) : ("once" as const);
};

/** Chats a message can go to: ones I can write in (a poll: not a direct chat). */
export const canForwardTo = (c: Pick<IChatConversation, "canSend" | "type">, messageType: string) =>
  c.canSend && !(messageType === "poll" && c.type === "direct");

/** Picking a chat toggles it; past the cap nothing is added. */
export const toggleForwardTarget = (selected: number[], id: number) =>
  selected.includes(id)
    ? selected.filter((x) => x !== id)
    : selected.length >= MAX_FORWARD_TARGETS
      ? selected
      : [...selected, id];
